import {BaseStore, globalStore, type StoreClass, StoreObject} from "../../../stem-core/state/Store";
import {field} from "../../../stem-core/state/StoreField";
import type {StemDate} from "../../../stem-core/time/Date";
import {apiClient, type MerchantScopedRequest} from "../../connection/BlinkApiClient";
import type {StoreId} from "../../../stem-core/state/State";
import {BaseEnum, makeEnum} from "../../../stem-core/state/BaseEnum";
import {BlinkGlobal} from "../../../blinkpay/UtilsLib";
import {filterStoreByMerchantAndLooseIdArray} from "../misc/Filter";
import {Batcher} from "../../../stem-core/base/Batcher";
import {VisibilityStatus} from "../misc/GenericEnums";
import {Merchant} from "../MerchantStore";

@makeEnum
export class AudienceNewsletterFrequency extends BaseEnum {
    static DAILY;
    static WEEKLY;
    static MONTHLY;
    static CUSTOM;
}

@globalStore
export class MerchantAudience extends BaseStore("MerchantAudience") {
    // TypedJSONField(MerchantUsersFilter) on the backend
    declare filters: any;
    @field(Number) merchantId: number;
    @field("Merchant") merchant: Merchant;
    @field(String) name: string;
    @field(VisibilityStatus) visibility: VisibilityStatus;
    @field(Boolean) isPublic: boolean;
    @field(AudienceNewsletterFrequency) defaultFrequency: AudienceNewsletterFrequency;
    @field(Array) defaultFrequencyOffsets: number[];
    @field(Date) defaultSendTime: StemDate;
    @field(Boolean) autosubscribeNewUsers: boolean;
    @field(Array) autosubscribeOnBenefitIds: string[];
    // `declare` is required: a bare declaration emits an undefined initializer after super(),
    // wiping the value StoreObject's constructor just set with Object.assign(this, obj).
    declare size?: number;

    toString(): string {
        return this.name;
    }

    getSize(): number | null {
        if (this.size >= 0) {
            // To protect from the -1 used as sentinel
            return this.size;
        }
        return null;
    }

    static statsBatcher = new Batcher(async (audiences: MerchantAudience[]) => {
        const response = await apiMerchantGetAudienceStats({audienceIds: audiences.map(audience => audience.id)});
        audiences.forEach((audience) => {
            Object.assign(audience, response[audience.id]);
            audience.dispatchChange();
        })
    });

    static allUnfiltered(): MerchantAudience[] {
        return super.all<MerchantAudience>();
    }

    static all<T extends StoreObject>(this: StoreClass<T>): T[] {
        return filterStoreByMerchantAndLooseIdArray(super.all<MerchantAudience>(), BlinkGlobal.iFrameState?.newsletter,
            (audience, idOrName) => audience.id == idOrName || audience.name == idOrName) as any;
    }

    static enqueueLoadStats(audience: MerchantAudience): void {
        if (audience.size != null) {
            return;
        }
        audience.size = -1; // Mark that we've enqueued this audience
        this.statsBatcher.process(audience);
    }
}

// MerchantCreateAudience (api/merchants/views.py)
export interface MerchantCreateAudienceRequest extends MerchantScopedRequest {
    name: string;
    isPublic: boolean;
    defaultFrequencyOffsets: number[];
    defaultFrequency?: AudienceNewsletterFrequency;
    defaultSendTime?: StemDate;
}

export async function apiMerchantCreateAudience(request: MerchantCreateAudienceRequest): Promise<any> {
    return apiClient.post("/merchant/create_audience", request);
}

// MerchantEditAudience. Nullable throughout because the call passes allowNulls: a field sent as null is
// cleared, one left out is untouched. filters is the backend's MerchantUsersFilter, which the store also
// leaves untyped
export interface MerchantEditAudienceRequest extends MerchantScopedRequest {
    audienceId: StoreId;
    name?: string | null;
    visibility?: VisibilityStatus | null;
    isPublic?: boolean | null;
    autosubscribeNewUsers?: boolean | null;
    autosubscribeOnBenefitIds?: number[] | null;
    defaultFrequency?: AudienceNewsletterFrequency | null;
    defaultFrequencyOffsets?: number[] | null;
    defaultSendTime?: StemDate | null;
    filters?: any;
}

export async function apiMerchantEditAudience(request: MerchantEditAudienceRequest): Promise<any> {
    return apiClient.post("/merchant/edit_audience", request, {allowNulls: true});
}

// MerchantDeleteAudience, the id on its own
export interface MerchantDeleteAudienceRequest extends MerchantScopedRequest {
    audienceId: StoreId;
}

export async function apiMerchantDeleteAudience(request: MerchantDeleteAudienceRequest): Promise<any> {
    return apiClient.post("/merchant/delete_audience", request);
}

export async function apiMerchantGetAudienceMembers(request: any): Promise<any> {
    return apiClient.get("/merchant/get_audience_members", request);
}

// What GetAudienceStatsRequest validates (dashboard/deserializers.py:52). Answers keyed by audience id,
// with the subscribed member count under size
export interface GetAudienceStatsRequest extends MerchantScopedRequest {
    audienceIds: StoreId[];
}

export async function apiMerchantGetAudienceStats(request: GetAudienceStatsRequest): Promise<Record<string, {size: number}>> {
    return apiClient.get("/merchant/get_audience_stats", request);
}
